import { useState, useRef, useEffect } from "react";
import "./ActivityLogIcon.css";
import CloseRed from "../CloseButton/CloseRed";
import { GetActivityLogs } from "../../../services/CommonApi";
import { getStatusStyle } from "../../../utils/statusStyles";
import { formatDateTime } from "../../../utils/dates";
import { baseURL } from "../../../services/api";
import { formatValue } from "../../../utils/cleanPayload";
import { useTranslation } from "react-i18next";
// import { formatDate, formatDateTime } from '../../../utils/dateFormatter';

const ActivityLogIcon = ({ objectId, modelName, name = "" }) => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch activity logs
  const fetchActivities = async () => {
    // Only fetch if both modelName and objectId are present
    if (!modelName || !objectId) {
      console.warn(
        "Cannot fetch activity logs: modelName or objectId is missing",
      );
      return;
    }

    try {
      const response = await GetActivityLogs(objectId, modelName);
      setActivities(response?.data);
    } catch (err) {
      console.error("Error fetching activity logs:", err);
    }
  };

  // Fetch logs whenever dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      fetchActivities();
    }
  }, [isDropdownOpen, modelName, objectId]); // Added dependencies

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setActivities([]); // Clear activities when closing
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Clear activities when dropdown closes
  useEffect(() => {
    if (!isDropdownOpen) {
      setActivities([]);
    }
  }, [isDropdownOpen]);

  // Utility: get initials
  const getUserInitials = (userName) =>
    userName ? userName.charAt(0).toUpperCase() : "?";

  // Utility: consistent avatar colors
  const getAvatarColor = (userName) => {
    const colors = [
      "linear-gradient(45deg, #4CAF50, #45a049)",
      "linear-gradient(45deg, #2196F3, #1976D2)",
      "linear-gradient(45deg, #FF9800, #F57C00)",
      "linear-gradient(45deg, #9C27B0, #7B1FA2)",
      "linear-gradient(45deg, #F44336, #D32F2F)",
      "linear-gradient(45deg, #00BCD4, #0097A7)",
    ];
    if (!userName) return colors[0];
    let hash = 0;
    for (let i = 0; i < userName.length; i++) {
      hash = userName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Group activities by date
  const groupActivitiesByDate = (data) => {
    const grouped = {};
    data.forEach((activity) => {
      const date = new Date(activity.timestamp).toDateString();
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(activity);
    });
    return grouped;
  };

  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // const formatDateTime = (timestamp) => {
  //     const date = new Date(timestamp);
  //     return date.toLocaleString("en-GB", {
  //         day: "2-digit",
  //         month: "long",
  //         year: "numeric",
  //         hour: "numeric",
  //         minute: "2-digit",
  //         hour12: true,
  //     });
  // };

  // Find created log
  const createdLog = activities.find((log) => log.action === "created");

  const formatTimeOnly = (timestamp) =>
    new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const groupedActivities = groupActivitiesByDate(activities);

  // Disable the icon if required props are missing
  const isDisabled = !modelName || !objectId;

  return (
    <div className="activity-log-container" ref={dropdownRef}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className={`activity-log-icon ${isDropdownOpen ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
        onClick={() => {
          if (!isDisabled) {
            setIsDropdownOpen(!isDropdownOpen);
          }
        }}
        style={{
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        <path
          d="M3 12C3 13.78 3.52784 15.5201 4.51677 17.0001C5.50571 18.4802 6.91131 19.6337 8.55585 20.3149C10.2004 20.9961 12.01 21.1743 13.7558 20.8271C15.5016 20.4798 17.1053 19.6226 18.364 18.364C19.6226 17.1053 20.4798 15.5016 20.8271 13.7558C21.1743 12.01 20.9961 10.2004 20.3149 8.55585C19.6337 6.91131 18.4802 5.50571 17.0001 4.51677C15.5201 3.52784 13.78 3 12 3C9.48395 3.00947 7.06897 3.99122 5.26 5.74L3 8"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3 3V8H8"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 7V12L16 14"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {isDropdownOpen && !isDisabled && (
        <div className="activity-log-dropdown">
          <div className="activity-log-content">
            <div className="activity-log-head">
              <span className="ftw-5">{t("Record Info")}</span>
              <CloseRed
                onClick={() => {
                  setIsDropdownOpen(false);
                  setActivities([]); // Clear activities when closing
                }}
              />
            </div>
            <div className="primary-container">
              <div className="primary-row">
                <span className="ftw-3">{t("Created on :")}</span>
                <span className="ftw-3">
                  {createdLog ? formatDateTime(createdLog.timestamp) : "--"}
                </span>
              </div>
              <div className="primary-row">
                <span className="ftw-3">{t("Created by :")}</span>
                <span className="activity-text ftw-3">
                  {createdLog?.user_name || "System"}
                </span>
              </div>
            </div>
            <div className="activity-log-head">
              <span className="ftw-5">{t("Activity Log")}</span>
            </div>

            <div className="secondary-container">
              {Object.entries(groupedActivities)
                .sort(([a], [b]) => new Date(b) - new Date(a))
                .map(([date, acts]) => (
                  <div key={date}>
                    <div className="date-header">{formatDateHeader(date)}</div>
                    {acts
                      .sort(
                        (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
                      )
                      .map((activity) => (
                        <div key={activity.id} className="activity-item">
                          <div className="activity-avatar ftw-6">
                            {activity.user_profile_pic ? (
                              <img
                                src={`${baseURL}${activity.user_profile_pic.startsWith("/") ? activity.user_profile_pic.slice(1) : activity.user_profile_pic}`}
                                alt={activity.user_name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  background: getAvatarColor(
                                    activity.user_name,
                                  ),
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "50%",
                                }}
                              >
                                {getUserInitials(activity.user_name)}
                              </div>
                            )}
                          </div>
                          <div className="activity-content">
                            <div className="activity-meta">
                              <span className="activity-user ftw-4">
                                {activity.user_name || "System"}
                              </span>
                              <span className="activity-time ftw-4">
                                {formatTimeOnly(activity.timestamp)}
                              </span>
                            </div>

                            {/* Only show field changes if available */}
                            {activity.action === "created" ? (
                              <div className="activity-changes">
                                <div className="activity-change ftw-4">
                                  {t(name)} {t("created")}
                                </div>
                              </div>
                            ) : (
                              activity.field_changes?.length > 0 && (
                                <div className="activity-changes">
                                  {activity.field_changes.map(
                                    (change, idx2) => (
                                      <div
                                        key={idx2}
                                        className="activity-change ftw-4"
                                      >
                                        <span className="field-name">
                                          {change.field_label}
                                        </span>
                                        <div className="value-change-row">
                                          <span className="old-value">
                                            {formatValue(change.old_value) ||
                                              change.old_value}
                                          </span>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                          >
                                            <path
                                              d="M4 12L20 12"
                                              stroke="#666"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                            <path
                                              d="M13 5L20 12L13 19"
                                              stroke="#666"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                          <span
                                            style={getStatusStyle(
                                              change.new_value,
                                            )}
                                            className="new-value"
                                          >
                                            {formatValue(change.new_value) ||
                                              change.new_value}
                                          </span>
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogIcon;
